/**
 * Rolling existing pipeline deals up into a portfolio, adding them to one, and taking them out
 * again (`POST /loans/rollup`, `POST /loans/{id}/detach`, `GET /loans/rollup/portfolios`).
 *
 * A portfolio's properties were only ever created new and empty; a deal already in the pipeline
 * could not be filed under one. The server does all of it, all or nothing, and a preview runs the
 * real change and takes it back, so every figure the dialog shows is the server's: nothing here
 * adds a total up or guesses a conflict.
 *
 * The answers are shaped by the server in camelCase already (the roll-up's own figures keep their
 * snake_case names, as `GET /underwriting-workbooks/deals/{id}/rollup` serves them), so nothing is
 * passed through `toCamelCase` here.
 */
import type { QueryClient } from '@tanstack/react-query'
import axios from 'axios'

import api from './api'
import { invalidateLoanQueries } from './loanQueryKeys'
import { LOAN_PORTFOLIO_KEY } from './portfolio'

/** Why a deal is refused: the server's codes, one of them per conflict. */
export type RollupReason =
  | 'not_found'
  | 'read_only_partition'
  | 'different_partition'
  | 'funded'
  | 'dead'
  | 'already_property'
  | 'portfolio_parent'
  | 'is_the_portfolio'
  | 'name_taken'
  | 'duplicate_name'
  | 'stage_gate'
  | 'not_a_property'
  | 'portfolio_not_a_portfolio'
  | 'portfolio_dead'
  | 'portfolio_funded'
  | 'portfolio_is_property'

export interface RollupConflict {
  loanId: string
  propertyName: string | null
  reason: RollupReason
  /** The server's sentence, ready to show. */
  detail: string
  /** `portfolio` when the refusal is about the target, `deal` when about a selected deal. */
  role: 'deal' | 'portfolio'
}

export interface RollupDeal {
  loanId: string
  propertyName: string | null
  location: string | null
  stage: string
  loanAmount: number | null
  status: 'ok' | 'blocked' | 'member'
  reasons: RollupReason[]
}

export interface RollupFigure {
  field: string
  label: string
  kind: string
  unit: string | null
  value: number | null
  partial_value: number | null
  complete: boolean
  coverage: { have: number; of: number }
}

export interface RollupSide {
  role: 'sponsor' | 'underwriter'
  label: string
  coverage: { have: number; of: number }
  figures: RollupFigure[]
}

/** The portfolio as the request leaves it, as the server computes it. */
export interface RollupResulting {
  propertyCount: number
  stage: string
  location: string | null
  assetType: string | null
  loanAmount: {
    /** What the portfolio's own record states (null: none). */
    portfolio: number | null
    /** What its live properties add up to, over those that state one. */
    propertiesTotal: number | null
    stated: number
    of: number
    /** Every live property states an amount. */
    complete: boolean
    /** The portfolio keeps an amount a person or a document gave it. */
    kept: boolean
    rule: string
  }
  units: {
    total: number | null
    partial: number | null
    stated: number
    of: number
    complete: boolean
  }
  /** Null when the roll-up of the underwriting models could not be read. */
  rollup: RollupSide[] | null
}

export interface RollupStageMove {
  loanId: string
  propertyName: string | null
  from: string
  to: string
}

export interface RollupResult {
  preview: boolean
  /** False when any deal (or the portfolio) is refused. */
  canRoll: boolean
  mode: 'new' | 'existing'
  /** Null in a preview of a new portfolio, which does not exist yet. */
  parentId: string | null
  parentName: string | null
  parentCreated: boolean
  adopted: string[]
  alreadyMembers: string[]
  /** Nothing to do: every deal is already a property of it. */
  noop: boolean
  deals: RollupDeal[]
  conflicts: RollupConflict[]
  stageMoves: RollupStageMove[]
  warnings: string[]
  resulting: RollupResulting | null
}

export interface DetachResult {
  preview: boolean
  canRoll: boolean
  parentId: string
  parentName: string | null
  released: string[]
  alreadyStandalone: string[]
  noop: boolean
  conflicts: RollupConflict[]
  deals: RollupDeal[]
  warnings: string[]
  resulting: RollupResulting | null
}

/** A portfolio a deal can be added to. */
export interface RollupPortfolio {
  id: string
  propertyName: string
  location: string | null
  stage: string
  loanAmount: number | null
  controlId: string | null
  propertyCount: number
  liveCount: number
}

/** Where the deals go: a new portfolio (named by the person) or one that exists. */
export type RollupTarget =
  | { mode: 'new'; name: string; location?: string; assetType?: string }
  | { mode: 'existing'; parentId: string }

export interface RollupInput {
  loanIds: string[]
  target: RollupTarget
  note?: string
}

function body(input: RollupInput, preview: boolean) {
  const target =
    input.target.mode === 'existing'
      ? { parent_loan_id: input.target.parentId }
      : {
          new_parent: {
            property_name: input.target.name.trim(),
            ...(input.target.location?.trim() ? { location: input.target.location.trim() } : {}),
            ...(input.target.assetType ? { asset_type: input.target.assetType } : {}),
          },
        }
  return {
    loan_ids: input.loanIds,
    ...target,
    ...(input.note?.trim() ? { note: input.note.trim() } : {}),
    ...(preview ? { preview: true } : {}),
  }
}

export const dealRollupService = {
  /** What rolling these deals up would do. Writes nothing. */
  preview: (input: RollupInput): Promise<RollupResult> =>
    api.post('/loans/rollup', body(input, true)).then((r) => r.data as RollupResult),

  /** Roll them up. All or nothing: a refusal is a 409 naming every deal (see `rollupProblem`). */
  rollUp: (input: RollupInput): Promise<RollupResult> =>
    api.post('/loans/rollup', body(input, false)).then((r) => r.data as RollupResult),

  /** Take properties out of a portfolio; each stands as a deal of its own again. */
  detach: (
    parentId: string,
    loanIds: string[],
    options: { note?: string; preview?: boolean } = {},
  ): Promise<DetachResult> =>
    api
      .post(`/loans/${parentId}/detach`, {
        loan_ids: loanIds,
        ...(options.note?.trim() ? { note: options.note.trim() } : {}),
        ...(options.preview ? { preview: true } : {}),
      })
      .then((r) => r.data as DetachResult),

  /** The portfolios a deal can be added to. */
  portfolios: (q?: string): Promise<RollupPortfolio[]> =>
    api
      .get('/loans/rollup/portfolios', { params: q?.trim() ? { q: q.trim() } : undefined })
      .then((r) => (r.data as { portfolios: RollupPortfolio[] }).portfolios),
}

/** What went wrong with a roll-up or a detach, in words, and the deals the server named. */
export interface RollupProblem {
  message: string
  conflicts: RollupConflict[]
}

export function rollupProblem(error: unknown): RollupProblem {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail
    if (detail && typeof detail === 'object' && 'conflicts' in detail) {
      const { message, conflicts } = detail as { message?: string; conflicts?: RollupConflict[] }
      return {
        message: message || 'The deals could not be rolled up. Nothing was changed.',
        conflicts: Array.isArray(conflicts) ? conflicts : [],
      }
    }
    if (typeof detail === 'string' && detail) return { message: detail, conflicts: [] }
    if (Array.isArray(detail) && detail.length) {
      const first = detail[0] as { msg?: string }
      return { message: first?.msg || 'The request was not understood.', conflicts: [] }
    }
  }
  return { message: 'The request failed. Nothing was changed.', conflicts: [] }
}

/** The short label a conflict's code is shown under. The server's `detail` says the rest. */
export const ROLLUP_REASON_LABEL: Record<RollupReason, string> = {
  not_found: 'Not in the pipeline',
  read_only_partition: 'Demo record',
  different_partition: 'Other environment',
  funded: 'Funded',
  dead: 'Dead deal',
  already_property: 'Already a property',
  portfolio_parent: 'Is a portfolio',
  is_the_portfolio: 'The portfolio itself',
  name_taken: 'Name already in portfolio',
  duplicate_name: 'Duplicate name',
  stage_gate: 'Stage move needs an admin',
  not_a_property: 'Not in this portfolio',
  portfolio_not_a_portfolio: 'Not a portfolio',
  portfolio_dead: 'Portfolio is dead',
  portfolio_funded: 'Portfolio is funded',
  portfolio_is_property: 'Portfolio is a property',
}

/**
 * Everything a roll-up or a detach changes: the deals and the portfolio (records, lineage,
 * journal), the pipeline list (the properties leave it, or come back), the portfolio summary and
 * every workbook list, section and roll-up (they share one key prefix).
 */
export async function refreshAfterRollup(
  queryClient: QueryClient,
  { parentId, loanIds }: { parentId: string | null; loanIds: string[] },
) {
  const ids = Array.from(new Set([...(parentId ? [parentId] : []), ...loanIds]))
  await Promise.all([
    ...ids.map((id) => invalidateLoanQueries(queryClient, id)),
    ...(parentId ? [queryClient.invalidateQueries({ queryKey: LOAN_PORTFOLIO_KEY(parentId) })] : []),
    queryClient.invalidateQueries({ queryKey: ['loan-portfolio'] }),
    queryClient.invalidateQueries({ queryKey: ['loan-underwriting-workbooks'] }),
    queryClient.invalidateQueries({ queryKey: ['rollup-portfolios'] }),
  ])
}
